import { appendBrandIds } from '@/lib/brand-filter-params';
import type { OrderPrintingSummary, OrderStatus } from '@/lib/orders-client';
import type { OrderDeliverySummary } from '@/lib/deliveries-client';
import type { DraftBlockerCode } from '@/lib/draft-order-client';
import type { SellChannel } from '@/types/sell-channel';

const API_BASE_URL = import.meta.env.VITE_API_URL || 'http://localhost:3002';

interface ApiOptions {
  method?: 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE';
  body?: unknown;
}

async function api<T>(endpoint: string, options: ApiOptions = {}): Promise<T> {
  const { method = 'GET', body } = options;
  const url = `${API_BASE_URL}${endpoint}`;

  const fetchOptions: RequestInit = {
    method,
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
  };

  if (body && method !== 'GET') {
    fetchOptions.body = JSON.stringify(body);
  }

  const response = await fetch(url, fetchOptions);

  if (response.status === 401 || response.status === 403) {
    window.dispatchEvent(new CustomEvent('auth:unauthorized', { detail: { status: response.status } }));
    throw new Error(`Unauthorized (${response.status})`);
  }

  if (!response.ok) {
    const errorText = await response.text();
    throw new Error(`API error (${response.status}): ${errorText}`);
  }

  return response.json();
}

export type FulfillmentType = 'delivery' | 'pickup';

/**
 * What we know about the money — mirrors the backend's `OrderPaymentState`. The board renders it
 * as a pill; the guidance strings behind these values live in the agent, not here.
 */
export type OrderPaymentState =
  | 'settled'
  | 'awaiting-our-link'
  | 'verifying-charge'
  | 'collected-by-store'
  | 'on-handover'
  | 'store-verifies-transfer'
  | 'needs-operator'
  | 'not-applicable';

export type PaymentPreference =
  'cash' | 'send_pos_for_card' | 'bank_transfer' | 'mercado_pago' | 'card' | 'pay_at_store';

/**
 * One card on the operator board — a projection of the order, never the order itself: the customer's
 * phone is deliberately absent, and the detail panel (which loads the full order) is one click away.
 * Dates arrive as ISO strings; the backend types them as `Date`.
 */
export interface OrderBoardCard {
  orderId: string;
  status: OrderStatus;
  channel: SellChannel;
  /** Delivery or pickup — null while a cart has not chosen yet. */
  fulfillmentType: FulfillmentType | null;
  /** When the customer placed it, or when the cart was opened for a draft. */
  placedAt: string;
  /** placedAt + the branch's ETA, when one is known. Null for carts and orders with no estimate. */
  promisedAt: string | null;
  etaMinutes: number | null;
  paymentState: OrderPaymentState;
  paymentPreference: PaymentPreference | null;
  total: number;
  currency: string;
  /** Total UNITS across the cart lines (not the number of lines). */
  itemCount: number;
  customerName: string | null;
  /** The chat this order lives on — null for a web cart with no conversation. */
  conversationId: string | null;
  /** The chat the customer came in from, when the cart was authored elsewhere. */
  originConversationId: string | null;
  brandId: string | null;
  brandName: string | null;
  locationId: string | null;
  locationName: string | null;
  createdAt: string;
  updatedAt: string;
  /** Carts only: when the draft stops being offered. */
  expiresAt: string | null;
  /**
   * Authored in the Playground, not by a real customer. The card says so and the lane still shows
   * it: hiding sandbox orders is what used to make the toolbar's pending badge point at an empty
   * board. Optional on the type so a card from an older backend reads as a real order.
   */
  isSandbox?: boolean;
  /** When the store accepted it. Optional so a card from an older backend reads as "unknown". */
  acceptedAt?: string | null;
  /** Thermal printing — the card shows it through `printStatusOf`. */
  printing?: OrderPrintingSummary | null;
  /** The rider booking (spec §4.6) — the card's delivery chip and the "En camino" lane read it. */
  delivery?: OrderDeliverySummary | null;
  /**
   * Carts only: the customer chose bank transfer and the draft is held until the receipt places it.
   * Absent on every other card, and on a card from an older backend.
   */
  transferAwait?: OrderBoardTransferAwait;
}

/** A cart held for its transfer receipt — see {@link OrderBoardCard.transferAwait}. */
export interface OrderBoardTransferAwait {
  /** When the draft started waiting for the transfer. */
  since: string;
  /** When the hold lapses, or null when it does not. */
  expiresAt: string | null;
  /** The receipt's verification so far — null while none has arrived. */
  receiptStatus: 'pending' | 'verified' | 'mismatch' | null;
  /** Placing the order after the receipt was blocked: an operator has to finish it. */
  needsReview: boolean;
  /** What blocked the placement, when `needsReview`. */
  blockers?: DraftBlockerCode[];
}

export interface OrderBoardPagination {
  page: number;
  limit: number;
  total: number;
  totalPages: number;
  hasMore: boolean;
}

export interface OrderBoardResponse {
  success: boolean;
  message: string;
  data: OrderBoardCard[];
  pagination: OrderBoardPagination;
}

/** The filters both board reads share. `status` is the lane's own column set (placed lanes only). */
export interface OrderBoardQuery {
  status?: OrderStatus[];
  channel?: SellChannel[];
  fulfillmentType?: FulfillmentType[];
  locationId?: string[];
  /** Aggregate sessions only: narrow to these brands (omitted = every brand in scope). */
  brandIds?: string[];
  placedFrom?: string;
  placedTo?: string;
  /** Attention lane: union in the DRAFT carts whose chat is in an active handoff (not windowed). */
  handoffCarts?: boolean;
  page?: number;
  limit?: number;
}

function boardParams(query: OrderBoardQuery): string {
  const params = new URLSearchParams();
  query.status?.forEach((status) => params.append('status', status));
  query.channel?.forEach((channel) => params.append('channel', channel));
  query.fulfillmentType?.forEach((type) => params.append('fulfillmentType', type));
  query.locationId?.forEach((id) => params.append('locationId', id));
  appendBrandIds(params, query.brandIds);
  if (query.placedFrom) params.set('placedFrom', query.placedFrom);
  if (query.placedTo) params.set('placedTo', query.placedTo);
  if (query.handoffCarts) params.set('handoffCarts', 'true');
  if (query.page) params.set('page', String(query.page));
  if (query.limit) params.set('limit', String(query.limit));
  const search = params.toString();
  return search ? `?${search}` : '';
}

export const orderBoardClient = {
  /** One lane of placed orders. `status` IS the column set — one request per lane. */
  async listLane(query: OrderBoardQuery): Promise<OrderBoardResponse> {
    return api<OrderBoardResponse>(`/orders/board${boardParams(query)}`);
  },

  /** The carts lane: live drafts that belong to no conversation (anonymous and identified web carts). */
  async listCarts(query: Omit<OrderBoardQuery, 'status'>): Promise<OrderBoardResponse> {
    return api<OrderBoardResponse>(`/orders/board/carts${boardParams(query)}`);
  },
};
